/** A non-blank record and the line it starts on, counted from 1. */
export type CsvRecord = { line: number; cells: string[] }

const SEPARATORS = ['\t', ';', ','] as const

/** The most frequent separator of the header line: tab (pasted cells), semicolon or comma. */
function detectSeparator(text: string): string {
  const header = text.split(/\r?\n|\r/, 1)[0] ?? ''
  const count = (separator: string) => header.split(separator).length
  return SEPARATORS.reduce(
    (best, separator) => (count(separator) > count(best) ? separator : best),
    ',',
  )
}

/** Parses CSV as spreadsheets export it: quoted cells may hold separators, quotes and line breaks. */
export function parseCsv(raw: string): CsvRecord[] {
  const text = raw.replace(/^\uFEFF/, '')
  const separator = detectSeparator(text)
  const records: CsvRecord[] = []
  let cells: string[] = []
  let cell = ''
  let quoted = false
  let line = 1
  let start = 1
  const endRecord = () => {
    cells.push(cell)
    if (cells.some((c) => c.trim() !== '')) records.push({ line: start, cells })
    cells = []
    cell = ''
  }
  for (let i = 0; i < text.length; i++) {
    const c = text[i]
    if (quoted) {
      if (c === '"' && text[i + 1] === '"') {
        cell += '"'
        i++
      } else if (c === '"') {
        quoted = false
      } else {
        if (c === '\n') line++
        cell += c
      }
    } else if (c === '"' && cell === '') {
      quoted = true
    } else if (c === separator) {
      cells.push(cell)
      cell = ''
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && text[i + 1] === '\n') i++
      endRecord()
      line++
      start = line
    } else {
      cell += c
    }
  }
  endRecord()
  return records
}

/** Decodes a file as UTF-8, or as Windows-1252 like the CSV export of Excel in French. */
export function decodeCsv(bytes: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    return new TextDecoder('windows-1252').decode(bytes)
  }
}
